import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HERO } from "@/constants/testIds";

export default function LiveStreamHero() {
  const [cfg, setCfg] = useState(null);
  const [reconnectKey, setReconnectKey] = useState(0);

  // Keep polling the stream config. On a 24/7 kiosk the backend may be briefly
  // unreachable (sleep/restart); re-fetching every 30s means the player loads
  // itself the moment the backend is back — no manual refresh needed.
  useEffect(() => {
    let alive = true;
    const load = () =>
      api
        .get("/stream")
        .then((r) => alive && setCfg(r.data))
        .catch(() => alive && setCfg((prev) => prev || { youtube_video_id: "" }));
    load();
    const t = setInterval(load, 30 * 1000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Periodically reconnect the YouTube embed so a stalled live player recovers
  // on its own overnight.
  useEffect(() => {
    const t = setInterval(() => setReconnectKey((k) => k + 1), 30 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const vid = cfg?.youtube_video_id;
  // When the channel ID is known, the live_stream embed always points at whatever is live right now —
  // immune to YouTube rotating the video ID after a reconnect, and resolved client-side so it never
  // depends on fragile server-side scraping. Fall back to a saved video ID only if no channel is set.
  const embedUrl = cfg?.channel_id
    ? `https://www.youtube.com/embed/live_stream?channel=${cfg.channel_id}&autoplay=1&mute=1&modestbranding=1&rel=0`
    : vid
    ? `https://www.youtube.com/embed/${vid}?autoplay=1&mute=1&modestbranding=1&rel=0`
    : null;

  return (
    <section
      data-testid={HERO.wrapper}
      className="relative w-full overflow-hidden"
    >
      {/* Lightning fallback background */}
      <div
        className="absolute inset-0 opacity-50"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/9751579/pexels-photo-9751579.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a1222]/70 via-[#0a1222]/40 to-[#0a1222]" />

      <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:py-14">
        <div className="mb-5 flex flex-wrap items-center gap-3">
          <div
            data-testid={HERO.livePill}
            className="flex items-center gap-2 rounded-full border border-[#d90429] bg-[#d90429]/10 px-3 py-1"
          >
            <span className="live-dot" />
            <span className="font-oswald text-xs font-semibold uppercase tracking-[0.3em] text-[#ff6b7a]">
              On Air Live
            </span>
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-gray-400">
            GAWX Channel · 24/7 Broadcast
          </div>
        </div>

        <h1 className="font-oswald text-4xl font-bold uppercase leading-none tracking-widest text-white sm:text-5xl lg:text-6xl">
          Weather. <span className="text-[#1f63c9]">Georgia.</span> Non‑Stop.
        </h1>
        <p className="mt-4 max-w-2xl text-base text-gray-300">
          Live radar, storm tracking, and continuous forecast coverage streaming to your screen — anywhere in the Peach State.
        </p>

        <div className="relative mt-8 aspect-video w-full overflow-hidden border border-white/10 bg-black shadow-[0_20px_60px_rgba(0,0,0,0.7)]">
          {embedUrl ? (
            <iframe
              key={reconnectKey}
              data-testid={HERO.livePlayer}
              src={embedUrl}
              title="Georgia Weather Network Live Stream"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="h-full w-full"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <div className="font-oswald text-2xl uppercase tracking-widest text-white">
                  Stream Loading
                </div>
                <div className="mt-1 text-sm text-gray-400">Configure the YouTube stream in admin.</div>
              </div>
            </div>
          )}
          <div className="pointer-events-none absolute left-3 top-3 flex items-center gap-2 rounded bg-black/70 px-2 py-1">
            <span className="live-dot" />
            <span className="font-oswald text-[10px] font-bold uppercase tracking-widest text-white">Live</span>
          </div>
        </div>
      </div>
    </section>
  );
}
